import type {
    AskVolleyHelpSummary,
    AskVolleyTurn,
    ContentRecommendRequest,
    HomePromptAnswer,
    HomePromptDTO,
    HomePromptEventType,
    HomePromptType,
    OffsetResult,
    RecommendedContent,
} from "@volley/data";

import fetchApi, { FetchError, logFetchError } from "../../util/fetchApi";

/** Where the prompt is shown — tags every analytics event for funnel analysis. */
export const SURFACE = "home_screen_widget";

/**
 * The same, for Ask Volley opened on its own rather than from a prompt.
 *
 * A separate surface rather than a flag in metadata because surface is the one
 * dimension every event carries and the reporting already splits on: an ask
 * from here has no prompt to segment by, so this is the only thing that tells
 * the two entry points apart.
 */
export const ASK_PAGE_SURFACE = "ask_volley_page";

/**
 * Fetch the single highest-priority prompt the user should see right now, or
 * null when there is nothing to show. `sportId` locks routing to the user's
 * currently-selected sport.
 */
export async function fetchNextPrompt(
    sportId?: number,
): Promise<HomePromptDTO | null> {
    const params = new URLSearchParams({ limit: "1" });
    if (sportId != null) {
        params.set("sportId", String(sportId));
    }
    const { prompts } = await fetchApi<{ prompts: HomePromptDTO[] }>(
        `/api/home-prompts/next?${params.toString()}`,
    );
    return prompts[0] ?? null;
}

/**
 * Resolve a content-prompt answer into the recommended content list. Returns an
 * empty array when the server has no recommendation for the answer (404), so
 * callers can render an "I couldn't find a match" state without special-casing.
 */
export async function fetchRecommendations(
    request: ContentRecommendRequest,
    limit = 10,
): Promise<RecommendedContent[]> {
    try {
        const result = await fetchApi<OffsetResult<RecommendedContent>>(
            `/api/content/recommend?limit=${limit}&offset=0`,
            "POST",
            request,
        );
        return result.result;
    } catch (e) {
        if (e instanceof FetchError && e.statusCode === 404) {
            return [];
        }
        logFetchError(e, "Failed to fetch content recommendations");
        return [];
    }
}

/**
 * Outcome of a free-text request.
 *
 * The server classifies the text first, so `results` now carries the intent
 * that produced it: RECOMMEND for workouts, HELP for support articles. Both
 * arrive in the same shape (help articles are content-index rows just as
 * workouts are), so the intent is what the caller branches its copy on rather
 * than sniffing card types.
 *
 * `not_workout_request` is the server's 422 — the text was neither a training
 * request nor a support question — and stays a distinct case so the caller can
 * offer support escalation instead of a generic "no match".
 *
 * `error` is a request that never produced an answer (timeout, 5xx, article
 * selection blowing up). It is deliberately NOT folded into an empty `results`:
 * the intent is unknown in that case, so the miss copy would tell a user who
 * asked a support question to "pick an option below" and never mention that
 * anything went wrong.
 *
 * It carries `helpAvailable` because the dead-end copy has to match what this
 * user can be offered: outside the classifier rollout only workouts are
 * reachable, and inviting them to ask about "using Volley" would point at a
 * route that does not exist for them.
 */
export type FreeTextResult =
    | {
          kind: "results";
          intent: "RECOMMEND" | "HELP";
          cards: RecommendedContent[];
          /**
           * The conversation this ask was recorded as a turn of. Echoed back on
           * the next ask so follow-ups land in the same conversation. Absent
           * from an older server, which has none.
           */
          conversationUlid?: string;
          /** The ask row a rating is recorded against; absent means unratable. */
          askId?: number;
          /**
           * A short answer to the question, grounded in the first card's
           * article. HELP only, and optional even there — an older server, a
           * disabled summariser, or an article that didn't actually answer the
           * question all arrive as `undefined`, and the caller falls back to
           * the generic lead-in above the cards.
           */
          summary?: string;
      }
    | {
          kind: "not_workout_request";
          helpAvailable: boolean;
          /** The conversation the rejected ask was still recorded as a turn of. */
          conversationUlid?: string;
      }
    // A request that never produced an answer. No conversation comes back
    // because none may have been opened — a timeout can fail before the server
    // ever saw the ask.
    | { kind: "error"; conversationUlid?: undefined };

type FreeTextResponse = OffsetResult<RecommendedContent> & {
    intent?: "RECOMMEND" | "HELP";
    helpSummary?: AskVolleyHelpSummary;
    conversationUlid?: string;
    askId?: number;
};

/**
 * Resolve the user's free-text request (the "Ask Volley anything" box) into
 * content via the server's classifier. A 422 means the input was unroutable;
 * any other failure (incl. 404 / model error) yields empty `results` so the
 * caller can render an "I couldn't find a match" state without special-casing.
 * The Gemini calls are slow, so the request gets a longer timeout than the
 * default fetch.
 */
export interface FreeTextAsk {
    /** The question being asked now. */
    text: string;
    /** The user's selected sport; locks results to it. */
    sportName?: string;
    /**
     * The Ask Volley impression this ask belongs to, so the server-side log
     * joins back to this impression's client events.
     */
    impressionUlid?: string;
    /**
     * Prior turns, oldest first, so a follow-up resolves against what came
     * before. Already trimmed to what the schema accepts; see toTurns.
     */
    thread?: AskVolleyTurn[];
    /** The conversation to continue; absent opens a new one server-side. */
    conversationUlid?: string;
    limit?: number;
}

/** Drop the keys the server treats as absent rather than sending them empty. */
function askBody(ask: FreeTextAsk): Record<string, unknown> {
    const { text, sportName, impressionUlid, thread, conversationUlid } = ask;
    return Object.fromEntries(
        Object.entries({
            text,
            sportName,
            impressionUlid,
            conversationUlid,
            thread: thread?.length ? thread : undefined,
        }).filter(([, v]) => v !== undefined && v !== ""),
    );
}

export async function fetchFreeTextRecommendations(
    ask: FreeTextAsk,
): Promise<FreeTextResult> {
    const limit = ask.limit ?? 4;
    try {
        const result = await fetchApi<FreeTextResponse>(
            `/api/content/recommend/free-text?limit=${limit}&offset=0`,
            "POST",
            askBody(ask),
            { signal: AbortSignal.timeout(60_000) },
        );
        return {
            kind: "results",
            // Default to RECOMMEND so a server running with the classifier
            // toggled off (no intent in the payload) renders exactly as before.
            intent: result.intent ?? "RECOMMEND",
            cards: result.result,
            ...(result.conversationUlid
                ? { conversationUlid: result.conversationUlid }
                : {}),
            ...(result.askId === undefined ? {} : { askId: result.askId }),
            ...(result.helpSummary?.text
                ? { summary: result.helpSummary.text }
                : {}),
        };
    } catch (e) {
        if (e instanceof FetchError && e.statusCode === 422) {
            // Absent on an older server: default to the narrower claim rather
            // than promising help this user may not be able to reach.
            const body = e.body as {
                helpAvailable?: boolean;
                conversationUlid?: string;
            } | null;
            return {
                kind: "not_workout_request",
                helpAvailable: body?.helpAvailable === true,
                ...(body?.conversationUlid
                    ? { conversationUlid: body.conversationUlid }
                    : {}),
            };
        }
        // A 404 is an older server without the route at all — there is nothing
        // wrong for the user to retry, so keep the pre-existing graceful miss.
        if (e instanceof FetchError && e.statusCode === 404) {
            return { kind: "results", intent: "RECOMMEND", cards: [] };
        }
        logFetchError(e, "Failed to fetch free-text recommendations");
        return { kind: "error" };
    }
}

/**
 * Submit a profile-prompt answer (profile prompts capture data rather than
 * routing to content). Best-effort: failures are logged, not surfaced.
 */
export async function submitProfileAnswer(
    homePromptId: number,
    answer: HomePromptAnswer,
    impressionUlid: string,
): Promise<void> {
    try {
        await fetchApi("/api/home-prompts/responses", "POST", {
            answers: [{ homePromptId, answer, impressionUlid }],
        });
    } catch (e) {
        logFetchError(e, "Failed to submit prompt answer");
    }
}

interface RecordEventArgs {
    eventType: HomePromptEventType;
    /**
     * The prompt being answered, when there is one. Absent for a session
     * opened from the launcher: nothing was prompted, so there is no id to
     * record, no type to snapshot and no prompt sport to keep.
     */
    prompt?: HomePromptDTO;
    impressionUlid: string;
    /**
     * The sport the user is currently looking at. This — not the prompt's own
     * sport — is the funnel dimension: a universal prompt has no sport, so
     * recording `prompt.sportId` makes per-sport engagement unanswerable for
     * exactly the prompts most users see. The selected sport lives only in the
     * client (it is not persisted to the profile), so the server cannot derive
     * it; the prompt's own sport is kept in metadata instead, which is what
     * answers "do sport-specific prompts beat universal ones".
     */
    sportId?: number | null;
    /** Where this happened. Defaults to the home-screen widget. */
    surface?: string;
    metadata?: Record<string, unknown>;
}

/**
 * Fire-and-forget funnel event. The endpoint returns 202 immediately and never
 * blocks UX; we mirror that by not awaiting and swallowing errors.
 */
export function recordPromptEvent({
    eventType,
    prompt,
    impressionUlid,
    sportId,
    surface = SURFACE,
    metadata,
}: RecordEventArgs): void {
    const payload: {
        eventType: HomePromptEventType;
        homePromptId: number | null;
        promptType: HomePromptType | null;
        sportId: number | null;
        impressionUlid: string;
        surface: string;
        metadata?: Record<string, unknown>;
    } = {
        eventType,
        homePromptId: prompt?.id ?? null,
        promptType: prompt?.promptType ?? null,
        sportId: sportId ?? null,
        impressionUlid,
        surface,
        // Only when there is a prompt: an absent key and a null one read the
        // same in the JSONB, and writing null here would make "no prompt" and
        // "a universal prompt" indistinguishable in the per-prompt reports.
        metadata: {
            ...(metadata ?? {}),
            ...(prompt ? { promptSportId: prompt.sportId } : {}),
        },
    };
    void fetchApi("/api/home-prompts/events", "POST", payload).catch((e) =>
        logFetchError(e, "Failed to record home prompt event"),
    );
}

/**
 * Close a conversation server-side, so its turns stop being replayed to the
 * models. Fire-and-forget: "New chat" clears the thread on screen immediately,
 * and a failure here costs a stale row, not the user's next question.
 */
export function closeConversation(conversationUlid: string): void {
    void fetchApi(
        `/api/ask-volley/conversations/${encodeURIComponent(conversationUlid)}/close`,
        "POST",
    ).catch((e) => logFetchError(e, "Failed to close Ask Volley conversation"));
}

/**
 * Record whether an answer helped.
 *
 * Fire-and-forget: the thumb is already shown as chosen, and a failed write is
 * not something the person who tapped it can do anything about. The cost of a
 * loss is one missing row in a rate, which is the right way round against
 * making someone retry a one-tap gesture.
 */
export function rateAsk(askId: number, helpful: boolean): void {
    void fetchApi(`/api/ask-volley/asks/${askId}/rating`, "POST", {
        helpful,
    }).catch((e) => logFetchError(e, "Failed to record Ask Volley rating"));
}

/**
 * Hand a conversation to a human, carrying the transcript.
 *
 * Returns the Intercom conversation the server opened (or had already opened),
 * so the caller can point the messenger straight at it, or null when it could
 * not. Null is a routine outcome rather than an error: the hand-off is behind
 * its own switch, and a player Intercom has never seen has no contact to open
 * one as. The caller falls back to the messenger, which is what shipped before
 * this existed — so the worst case is the old behaviour rather than a dead end
 * at the exact moment someone has already been let down once.
 */
export async function escalateConversation(
    conversationUlid: string,
): Promise<string | null> {
    try {
        const { intercomConversationId } = await fetchApi<{
            intercomConversationId?: string;
        }>(
            `/api/ask-volley/conversations/${encodeURIComponent(
                conversationUlid,
            )}/escalate`,
            "POST",
        );
        return intercomConversationId ?? null;
    } catch (e) {
        logFetchError(e, "Failed to hand the Ask Volley conversation over");
        return null;
    }
}
